import Tone from 'tone';
import {setEffectParameterValues} from 'actions/index.js';
import {bulkUpdate} from 'utils/bulkUpdate.js';
import {mate} from 'genetix/Flip.js';
import {AbstractParameter, IntegerParameter} from 'parameters/AbstractParameter.js';
import {loadParameterFromJson, saveParameterToJson} from 'parameters/parameterJsonFunctions.js';
import {uuid} from 'uuidv4';

const DEFAULT_CURVE = 'linear';
var envelopeCount = 0;
var disposedEnvelopeCount = 0;

window.FADER = 0.05;

class SynthEnvelope  {
    constructor(sequencerEngine, mixSession, context, audioNode, min, max, envelope) {
        this.sequencerEngine = sequencerEngine;
        this.store = this.sequencerEngine.store;
        this.id = uuid();
        this.mixSession = mixSession;
        this.context = context.getCopy()
            .forSubDevice(this);

        if (envelope) {
            this.envelope = envelope;
        } else {
            this.envelope = new Tone.ScaledEnvelope();
        }

        if (audioNode !== undefined) {
            this.audioNode = audioNode;
            this.envelope.connect(audioNode);
        }

        this.min = min;
        this.max = max;

        this.parameters = [];
        this.setupParameters();

        this.savedFrequency = undefined;
    }

    dispose() {
        this.parameters.forEach(
            p => p.dispose(true));
        this.envelope.dispose();
        this.envelope = undefined;
        this.parameters = undefined;
    }

    connect(audioNode) {
        this.envelope.connect(audioNode);
    }

    disconnect(audioNode=this.audioNode) {
        this.envelope.disconnect(audioNode);
    }

    set frequency(x) {
        this.savedFrequency = x;

        let baseFrequency = undefined
        if (this.level > 0) {
            if (this.max !== undefined) {
                baseFrequency = x + this.level * (this.max - x);
            } else {
                baseFrequency = x + this.level * x * 2;
            }
        } else {
            if (this.min !== undefined && this.min < 0) {
                baseFrequency = -1 *
                    this.level * (this.min + x);
            } else {
                baseFrequency = this.level * (x);
            }
        }

        this.envelope.min = x*2;
        if (!isNaN(baseFrequency)) {
            this.envelope.max = baseFrequency;
        }
    }

    set level(x) {
        this.levelParameter.set(x, true, true);
    }

    triggerAttack(time, velocity) {
        this.envelope.triggerAttack(time, velocity);
    }

    triggerAttackRelease(duration, time, velocity) {
        time = Math.max(time, 0);

        this.envelope.triggerAttackRelease(duration, time, velocity);
    }

    triggerRelease(time) {
        time = Math.max(time, 0);
        this.envelope.triggerRelease(time);
    }

    setupParameters() {
        this.attackParameter = new AbstractParameter(
            'Attack',
            this.envelope, (a, b) => {
                if (!this.envelope) {
                    return;
                }
                this.envelope.attack = (b + 0.000001) / 1000.0;
            },
            {
                min: 0,
                max: 2000,
                unit: 'ms',
                defaultValue: 10,
            });

        this.registerParameter(this.attackParameter);

        this.decayParameter = new AbstractParameter(
            'Decay',
            this.envelope, (a, b) => {
                if (!this.envelope) {
                    return;
                }
                this.envelope.decay = (b + 0.00001) / 1000.0;
            },
            {
                min: 0,
                max: 3000,
                unit: 'ms',
                defaultValue: 500
            });

        this.registerParameter(this.decayParameter);

        this.sustainParameter = new AbstractParameter(
            'Sustain',
            this.envelope, (a, b) => {
                if (!this.envelope) {
                    return;
                }
                this.envelope.sustain = Math.min(b, 0.999);
            }, {
                min: 0,
                max: 1,
                unit: '',
                defaultValue: 0.8
            });
            
        this.registerParameter(this.sustainParameter);

        this.releaseParameter = new AbstractParameter(
            'Release',
            this.envelope, (a, b) => {
                if (!this.envelope) {
                    return;
                }
                this.envelope.release = (b + 0.000001) / 1000.0;
            }, {
                min: 0,
                max: 8000,
                unit: 'ms',
                defaultValue: 200
            });

        this.registerParameter(this.releaseParameter);

        this.attackCurveParameter = new AbstractParameter(
            'Attack Curve',
            this.envelope, (a, b) => {
                if (!this.envelope) {
                    return;
                }
                a.attackCurve = b;
            },
            {
                min: 0,
                max: 10000,
                unit: '',
                defaultValue: DEFAULT_CURVE,
                automatable: false,
            });

        this.registerParameter(this.attackCurveParameter);

        this.decayCurveParameter = new AbstractParameter(
            'Decay Curve',
            this.envelope, (a, b) => a.decayCurve = b,
            {
                min: 0,
                max: 10000,
                unit: '',
                defaultValue: DEFAULT_CURVE,
                automatable: false,
            });

        this.registerParameter(this.decayCurveParameter);

        this.releaseCurveParameter = new AbstractParameter(
            'Release Curve',
            this.envelope, (a, b) => a.releaseCurve = b,
            {
                min: 0,
                max: 10000,
                unit: '',
                defaultValue: DEFAULT_CURVE,
                automatable: false,
            });

        this.registerParameter(this.releaseCurveParameter);

        this.levelParameter = new AbstractParameter(
            'Level',
            this, (a, b) => {
                if (!this.envelope) {
                    return;
                }
                this.frequency = this.savedFrequency || 0;
            }, {
                min: -1,
                max: 1,
                unit: '',
                defaultValue: 0
            });

        this.registerParameter(this.levelParameter);
    }

    refresh() {
        this.attack = this.attack;
        this.decay = this.decay;
        this.level = this.level;
        this.release = this.release;
        this.sustain = this.sustain;
    }

    get attack() {
        return this.attackParameter.get();
    }

    set attack(x) {
        this.attackParameter.set(x, true, true);
    }

    get decay() {
        return this.decayParameter.get();
    }

    set decay(x) {
        this.decayParameter.set(x, true, true);
    }

    get sustain() {
        return this.sustainParameter.get();
    }

    set sustain(x) {
        this.sustainParameter.set(x, true, true);
    }

    get release() {
        return this.releaseParameter.get();
    }

    set release(x) {
        this.releaseParameter.set(x, true, true);
    }

    get attackCurve() {
        return this.attackCurveParameter.get();
    }

    set attackCurve(x) {
        this.attackCurveParameter.set(x, true, true);
    }

    get decayCurve() {
        return this.decayCurveParameter.get();
    }

    set decayCurve(x) {
        this.decayCurveParameter.set(x, true, true);
    }

    get releaseCurve() {
        return this.releaseCurveParameter.get();
    }

    set releaseCurve(x) {
        this.releaseCurveParameter.set(x, true, true);
    }

    get level() {
        if (this.levelParameter === undefined ||
            this.levelParameter.get(true) === undefined) {
            return 0;
        }
        return this.levelParameter.get(true);
    }

    set level(x) {
        this.levelParameter.set(x, true, true);
    }

    registerParameter(parameter) {
        parameter.setStore(this.store);
        parameter.setMixSession(this.mixSession);
        parameter.setContext(this.context);
        parameter.setTrack(this.context.track);
        this.parameters.push(parameter);
    }

    getJson(isPreset) {
        let json = {
            level: this.level,
            attack: this.attack,
            decay: this.decay,
            sustain: this.sustain,
            release: this.release,
            attackCurve: this.attackCurve,
            decayCurve: this.decayCurve,
            releaseCurve: this.releaseCurve,
            x: this.x,
            y: this.y,
            width: this.width,
            height: this.height
        };

        json = saveParameterToJson(json, 'attackParameter', this.attackParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'decayParameter', this.decayParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'sustainParameter', this.sustainParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'releaseParameter', this.releaseParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'attackCurveParameter', this.attackCurveParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'decayCurveParameter', this.decayCurveParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'releaseCurveParameter', this.releaseCurveParameter, undefined, isPreset);
        json = saveParameterToJson(json, 'levelParameter', this.levelParameter, undefined, isPreset);
        return json;
    }

    fromJson(json, ignoreId) {
        let bulk = {};

        this.x = json.x;
        this.y = json.y;
        this.width = json.width;
        this.height= json.height;

        bulkUpdate(this.attackParameter, json.attack, bulk);
        bulkUpdate(this.decayParameter, json.decay, bulk);
        bulkUpdate(this.sustainParameter, json.sustain, bulk);
        bulkUpdate(this.releaseParameter, json.release, bulk);
        bulk = {
            ... bulk,
            ... loadParameterFromJson(json, 'attackParameter', this.attackParameter, this.sequencerEngine, ignoreId, true),
            ... loadParameterFromJson(json, 'decayParameter', this.decayParameter, this.sequencerEngine, ignoreId, true),
            ... loadParameterFromJson(json, 'sustainParameter', this.sustainParameter, this.sequencerEngine, ignoreId, true),
            ... loadParameterFromJson(json, 'releaseParameter', this.releaseParameter, this.sequencerEngine, ignoreId, true),
            //... loadParameterFromJson(json, 'attackCurveParameter', this.attackCurveParameter, this.sequencerEngine, false, true),
            //... loadParameterFromJson(json, 'decayCurveParameter', this.decayCurveParameter, this.sequencerEngine, false, true),
            //... loadParameterFromJson(json, 'releaseCurveParameter', this.releaseCurveParameter, this.sequencerEngine, false, true),
        };

        if (Tone.Master.context.isOffline) {
            bulkUpdate(this.levelParameter, json.level, {});
            loadParameterFromJson(json, 'levelParameter', this.levelParameter, this.sequencerEngine, ignoreId, true);
        } else {
            bulkUpdate(this.levelParameter, json.level, bulk);
            bulk = {
                ... bulk,
                ... loadParameterFromJson(json, 'levelParameter', this.levelParameter, this.sequencerEngine, ignoreId, true)
            };
        }

        return bulk;
    }

    refresh() {
        this.parameters.filter(p => p.isConnectedToAnyLFO())
            .forEach(p => p.callSetter());
    }

    mateJson(partner, probability) {
        this.mateParameter('attack', partner, this.attackParameter, probability);
        this.mateParameter('decay', partner, this.decayParameter, probability);
        this.mateParameter('sustain', partner, this.sustainParameter, probability);
        this.mateParameter('release', partner, this.releaseParameter, probability);
    }

    mateParameter(name, data, param1, probability) {
        mate(this.sequencerEngine, name, data, param1, probability);
    }
}

export default SynthEnvelope;
